import { supabase } from "@/integrations/supabase/client";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "./use-toast";
import { ExperienceSection } from "@/types/experience-section";

/**
 * Client-side slug generator. Mirrors the DB `slugify_section` for
 * predictable UX in the admin form. Server still validates uniqueness.
 */
export const slugifyTitle = (title: string): string => {
  return (title || "")
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "") || "section";
};

export const useExperienceSections = () => {
  const { data: sections, isLoading, error } = useQuery({
    queryKey: ["experience-sections"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("experience_sections")
        .select("*")
        .eq("is_active", true)
        .order("display_order", { ascending: true });

      if (error) {
        console.error("Error fetching experience sections:", error);
        throw error;
      }

      return data as ExperienceSection[];
    },
  });

  return {
    sections: sections || [],
    isLoading,
    error,
  };
};

export const useAdminExperienceSections = () => {
  const queryClient = useQueryClient();

  const { data: sections, isLoading, error } = useQuery({
    queryKey: ["admin-experience-sections"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("experience_sections")
        .select("*")
        .order("display_order", { ascending: true });

      if (error) {
        console.error("Error fetching admin experience sections:", error);
        throw error;
      }

      return data as ExperienceSection[];
    },
  });

  const createSection = useMutation({
    mutationFn: async (section: Omit<Partial<ExperienceSection>, 'id' | 'created_at' | 'updated_at'> & { title: string }) => {
      const slug = section.slug?.trim() || slugifyTitle(section.title);
      const { data, error } = await supabase
        .from("experience_sections")
        .insert({
          title: section.title,
          subtitle: section.subtitle,
          tag: section.tag,
          section_type: section.section_type || 'tag_based',
          is_active: section.is_active ?? true,
          display_order: section.display_order ?? 0,
          icon: section.icon,
          slug,
          enable_interest_signup: section.enable_interest_signup ?? true,
          early_bird_enabled: section.early_bird_enabled ?? null,
          early_bird_days_before: section.early_bird_days_before ?? null,
          early_bird_discount_percent: section.early_bird_discount_percent ?? null,
          bring_friend_eligible: section.bring_friend_eligible ?? null,
        })
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-experience-sections"] });
      queryClient.invalidateQueries({ queryKey: ["experience-sections"] });
      toast({ title: "Section created successfully" });
    },
    onError: (error: Error) => {
      toast({ title: "Failed to create section", description: error.message, variant: "destructive" });
    },
  });

  const updateSection = useMutation({
    mutationFn: async ({ id, ...updates }: Partial<ExperienceSection> & { id: string }) => {
      const { data, error } = await supabase
        .from("experience_sections")
        .update(updates)
        .eq("id", id)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-experience-sections"] });
      queryClient.invalidateQueries({ queryKey: ["experience-sections"] });
      toast({ title: "Section updated successfully" });
    },
    onError: (error: Error) => {
      toast({ title: "Failed to update section", description: error.message, variant: "destructive" });
    },
  });

  const deleteSection = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from("experience_sections")
        .delete()
        .eq("id", id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-experience-sections"] });
      queryClient.invalidateQueries({ queryKey: ["experience-sections"] });
      toast({ title: "Section deleted successfully" });
    },
    onError: (error: Error) => {
      toast({ title: "Failed to delete section", description: error.message, variant: "destructive" });
    },
  });

  const reorderSections = useMutation({
    mutationFn: async (orderedIds: string[]) => {
      // Update each section's display_order
      const updates = orderedIds.map((id, index) => 
        supabase
          .from("experience_sections")
          .update({ display_order: index + 1 })
          .eq("id", id)
      );

      await Promise.all(updates);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-experience-sections"] });
      queryClient.invalidateQueries({ queryKey: ["experience-sections"] });
      toast({ title: "Sections reordered successfully" });
    },
    onError: (error: Error) => {
      toast({ title: "Failed to reorder sections", description: error.message, variant: "destructive" });
    },
  });

  return {
    sections: sections || [],
    isLoading,
    error,
    createSection,
    updateSection,
    deleteSection,
    reorderSections,
  };
};
